// This file is a fully vibe-coded throwaway debug utility and must not be extended to be load-bearing for any functionality.
// No attempt should be made at maintaining this file or even to be read as an example for similar feature implementation.
// Changes to this file should be one-shot re-prompted from scratch instead of wasting machine and/or human tokens on reading it.

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

export const Route = createFileRoute('/_app/debug/cache/')({
  component: CacheExplorer,
});

function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
): HTMLElementTagNameMap[K] {
  return document.createElement(tag);
}

function addDeleteOnBulletClick(
  li: HTMLLIElement,
  parent: FileSystemDirectoryHandle,
  name: string,
) {
  li.addEventListener('click', async (e) => {
    if (e.target !== li) return;
    if (!confirm(`Delete "${name}"?`)) return;
    await parent.removeEntry(name, { recursive: true });
    li.remove();
  });
}

function createFileNode(
  name: string,
  handle: FileSystemFileHandle,
  parent: FileSystemDirectoryHandle,
): HTMLLIElement {
  const li = createElement('li');
  addDeleteOnBulletClick(li, parent, name);

  const a = createElement('a');
  a.textContent = name;
  a.href = '#';
  a.addEventListener('click', async (e) => {
    if (a.dataset.ready) return;
    e.preventDefault();
    const file = await handle.getFile();
    a.href = URL.createObjectURL(file);
    a.download = file.name;
    a.dataset.ready = '1';
    a.click();
  });

  const sizeText = document.createTextNode('');
  handle.getFile().then((file) => {
    sizeText.textContent = ` (${file.size} bytes)`;
  });

  li.appendChild(a);
  li.appendChild(sizeText);
  return li;
}

function createDirNode(
  name: string,
  handle: FileSystemDirectoryHandle,
  parent: FileSystemDirectoryHandle,
): HTMLLIElement {
  const li = createElement('li');
  addDeleteOnBulletClick(li, parent, name);

  const details = createElement('details');
  const summary = createElement('summary');
  summary.textContent = name;
  details.appendChild(summary);

  const ul = createElement('ul');
  details.appendChild(ul);

  let loaded = false;
  details.addEventListener('toggle', async () => {
    if (!details.open || loaded) return;
    loaded = true;

    const entries: Array<[string, FileSystemHandle]> = [];
    for await (const entry of handle.entries()) {
      entries.push(entry);
    }
    entries.sort(([an, ah], [bn, bh]) => {
      if (ah.kind !== bh.kind) return ah.kind === 'directory' ? -1 : 1;
      return an.localeCompare(bn);
    });

    for (const [childName, childHandle] of entries) {
      ul.appendChild(
        childHandle.kind === 'directory'
          ? createDirNode(
              childName,
              childHandle as FileSystemDirectoryHandle,
              handle,
            )
          : createFileNode(
              childName,
              childHandle as FileSystemFileHandle,
              handle,
            ),
      );
    }
  });

  li.appendChild(details);
  return li;
}

async function buildTree(container: HTMLUListElement) {
  container.innerHTML = '';

  try {
    const root = await navigator.storage.getDirectory();

    const entries: Array<[string, FileSystemHandle]> = [];
    for await (const entry of root.entries()) {
      entries.push(entry);
    }
    entries.sort(([an, ah], [bn, bh]) => {
      if (ah.kind !== bh.kind) return ah.kind === 'directory' ? -1 : 1;
      return an.localeCompare(bn);
    });

    for (const [name, handle] of entries) {
      container.appendChild(
        handle.kind === 'directory'
          ? createDirNode(name, handle as FileSystemDirectoryHandle, root)
          : createFileNode(name, handle as FileSystemFileHandle, root),
      );
    }
  } catch {
    const err = createElement('li');
    err.textContent = 'Failed to read OPFS root.';
    container.appendChild(err);
  }
}

function CacheExplorer() {
  const listRef = useRef<HTMLUListElement>(null);
  const estimateRef = useRef<HTMLParagraphElement>(null);

  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    buildTree(listRef.current!);
    navigator.storage.estimate().then(({ usage, quota }) => {
      if (!estimateRef.current) return;
      const fmt = (n?: number) =>
        n == null ? '?' : `${n.toLocaleString()} bytes`;
      estimateRef.current.textContent = `Origin storage: ${fmt(usage)} used / ${fmt(quota)} quota`;
    });
  }, []);

  const colorScheme =
    localStorage.getItem('khorium-theme') === 'dark' ? 'dark' : 'light';

  return (
    <>
      <style>{`
        #tree, #tree * { all: revert; color-scheme: ${colorScheme}; }
        #tree li { list-style-type: '🗑️ '; }
      `}</style>
      <p ref={estimateRef}>Loading storage estimate…</p>
      <ul id="tree" ref={listRef} />
    </>
  );
}
